import axios from "axios";
import { useEffect, useState } from "react";
import Form from "react-bootstrap/Form";
import Timer from "../../../components/Timer";
import { useDataContext } from "../../../utils/DataContext";
import "../../../styles/DatasetPage.css";
import { buttonText } from "../../../utils/buttonText";
import { notificationText } from "../../../utils/notificationText";

function TextToImagePage(props) {
  const { item, loadDatasetList } = props;
  const { userToken } = useDataContext();

  const [prompt, setPrompt] = useState("");
  const [numImages, setNumImages] = useState(1);
  const [model, setModel] = useState("");
  const [samples, setSamples] = useState([]);
  const [selectedSampleIds, setSelectedSampleIds] = useState([]);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [loadingSamples, setLoadingSamples] = useState(false);
  const [loading, setLoading] = useState(false);
  const [previewImages, setPreviewImages] = useState([]);
  const [errorMsg, setErrorMsg] = useState("");

  const requiresSourceImage = model.toLowerCase().includes("edit");
  const modelLower = model.toLowerCase();
  const allowMultiSource =
    modelLower.includes("gemini") || modelLower.includes("qwen-image");
  const maxSourceImages = modelLower.includes("gemini")
    ? 14
    : modelLower.includes("qwen-image")
      ? 3
      : 1;
  const usingSourceImage = selectedSampleIds.length > 0;

  useEffect(() => {
    if (!userToken) {
      setLoadingConfig(false);
      return;
    }
    setLoadingConfig(true);
    axios
      .get("/datasets/image-generation-config", {
        params: {
          dataset_name: item.name,
          requesterToken: userToken,
        },
      })
      .then((response) => setModel(response.data?.model || ""))
      .catch((error) => {
        const raw = error.response?.data || error.message;
        setErrorMsg(
          typeof raw === "string" ? raw : JSON.stringify(raw, null, 2)
        );
      })
      .finally(() => setLoadingConfig(false));
  }, [item.name, userToken]);

  useEffect(() => {
    if (!userToken) return;

    let active = true;
    setLoadingSamples(true);
    axios
      .get("/datasets/image-samples", {
        params: {
          dataset_name: item.name,
          requesterToken: userToken,
        },
      })
      .then((response) => {
        if (!active) return;
        const nextSamples = response.data || [];
        setSamples(nextSamples);
        setSelectedSampleIds((prev) =>
          prev.filter((id) => nextSamples.some((sample) => sample.id === id))
        );
      })
      .catch((error) => {
        if (!active) return;
        const raw = error.response?.data || error.message;
        setErrorMsg(
          typeof raw === "string" ? raw : JSON.stringify(raw, null, 2)
        );
      })
      .finally(() => {
        if (active) setLoadingSamples(false);
      });
    return () => {
      active = false;
    };
  }, [item.name, userToken]);

  useEffect(() => {
    if (selectedSampleIds.length > maxSourceImages) {
      setSelectedSampleIds((prev) => prev.slice(0, maxSourceImages));
    }
  }, [maxSourceImages, selectedSampleIds.length]);

  const toggleSample = (sampleId) => {
    setSelectedSampleIds((prev) => {
      if (allowMultiSource) {
        if (prev.includes(sampleId)) {
          return prev.filter((id) => id !== sampleId);
        }
        if (prev.length >= maxSourceImages) {
          setErrorMsg(
            `At most ${maxSourceImages} source image(s) for this model`
          );
          return prev;
        }
        return [...prev, sampleId];
      }
      return prev[0] === sampleId ? [] : [sampleId];
    });
  };

  const generateImages = async () => {
    if (!prompt.trim()) {
      setErrorMsg("Please enter a prompt");
      return;
    }
    if (requiresSourceImage && !selectedSampleIds.length) {
      setErrorMsg(
        allowMultiSource
          ? "Please select at least one source image"
          : "Please select a source image"
      );
      return;
    }
    setLoading(true);
    setPreviewImages([]);
    setErrorMsg("");
    try {
      const response = await axios.post("/datasets/generate/text-to-image", {
        dataset_name: item.name,
        prompt: prompt.trim(),
        num_images: Number(numImages),
        sample_ids: selectedSampleIds.length ? selectedSampleIds : undefined,
        sample_id: selectedSampleIds[0] || undefined,
        requesterToken: userToken,
      });
      const images = response.data?.images || [];
      setPreviewImages(images);
      loadDatasetList();
    } catch (error) {
      const raw = error.response?.data || error.message;
      setErrorMsg(typeof raw === "string" ? raw : JSON.stringify(raw, null, 2));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="stable-diffusion-page text-to-image-page">
      <p>
        {requiresSourceImage
          ? allowMultiSource
            ? "Select one or more images from this dataset, then describe the changes to make."
            : "Select one image from this dataset, then describe the changes to make."
          : allowMultiSource
            ? "Optionally select one or more source images for editing, or leave empty for text-to-image generation."
            : "Optionally select a source image for editing, or leave it empty for text-to-image generation."}
      </p>
      {loadingConfig ? (
        <Timer loadingStates={[loadingConfig]} />
      ) : (
        model && <div className="tti-model-badge">Model: {model}</div>
      )}
      <div className="tti-source-section">
        <Form.Label>
          Source image
          {requiresSourceImage ? " (required)" : " (optional)"}
          {allowMultiSource ? ` · multi-select (max ${maxSourceImages})` : ""}
        </Form.Label>
        {loadingSamples ? (
          <Timer loadingStates={[loadingSamples]} />
        ) : samples.length ? (
          <div className="tti-source-grid">
            {samples.map((sample) => (
              <button
                type="button"
                key={sample.id}
                className={`tti-source-item ${
                  selectedSampleIds.includes(sample.id) ? "selected" : ""
                }`}
                onClick={() => toggleSample(sample.id)}
                aria-pressed={selectedSampleIds.includes(sample.id)}
                title={sample.name}
              >
                <img src={sample.preview_url} alt={sample.name} />
                <span>{sample.name}</span>
              </button>
            ))}
          </div>
        ) : (
          <div className="tti-source-empty">No source images found.</div>
        )}
      </div>
      <Form.Group className="mb-3">
        <Form.Label>{usingSourceImage ? "Edit prompt" : "Prompt"}</Form.Label>
        <Form.Control
          as="textarea"
          rows={3}
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder={
            usingSourceImage
              ? "e.g. replace the background with a clean white studio"
              : "e.g. a photo of a cat sitting on a chair"
          }
        />
      </Form.Group>
      <div className="sd-params-row">
        <Form.Group>
          <Form.Label>Count</Form.Label>
          <Form.Control
            type="number"
            min={1}
            value={numImages}
            onChange={(e) => setNumImages(e.target.value)}
          />
        </Form.Group>
      </div>
      <button
        type="button"
        className="org-btn org-btn-primary"
        onClick={generateImages}
        disabled={
          loading ||
          loadingConfig ||
          loadingSamples ||
          (requiresSourceImage && !samples.length)
        }
      >
        {buttonText(loading ? (
          <span>
            <Timer loadingStates={[loading]} />
          </span>
        ) : usingSourceImage ? (
          "Edit Image"
        ) : (
          "Generate"
        ))}
      </button>
      {errorMsg && (
        <div className="tti-error-box">
          <pre>{notificationText(errorMsg)}</pre>
        </div>
      )}
      {previewImages.length > 0 && (
        <div className="sd-preview-grid">
          {previewImages.map((img, idx) => (
            <img
              key={idx}
              src={`data:image/png;base64,${img}`}
              alt={`generated-${idx}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default TextToImagePage;
